import React from 'react';

const getScoreBadge = (score, passed) => {
  const dynamicStyle = passed 
    ? { background: 'rgba(130,255,207,0.15)', color: 'var(--tqa-green)' } 
    : score >= 50 
      ? { background: 'rgba(234,179,8,0.15)', color: '#fbbf24' } 
      : { background: 'rgba(239,68,68,0.15)', color: '#f87171' };
      
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium`} style={dynamicStyle}>
      {score}%
    </span>
  );
};

const scoreColor = (score) => {
  if (score >= 70) return { color: 'var(--tqa-green)' };
  if (score >= 50) return { color: '#fbbf24' };
  return { color: '#f87171' };
};

const scoreBg = (score) => {
  if (score >= 70) return { background: 'rgba(130,255,207,0.05)', borderColor: 'rgba(130,255,207,0.2)' };
  if (score >= 50) return { background: 'rgba(234,179,8,0.05)', borderColor: 'rgba(234,179,8,0.2)' };
  return { background: 'rgba(239,68,68,0.05)', borderColor: 'rgba(239,68,68,0.2)' };
};

/* ── Single Run Detail View ──────────────────────────────── */
const SingleRunView = ({ run }) => (
  <div className="space-y-5">
    {/* Metrics Bar */}
    <div className="flex items-center gap-4 flex-wrap">
      {getScoreBadge(run.score, run.passed)}
      <div className="flex items-center gap-3 text-xs" style={{color:'rgba(255,255,255,0.45)'}}>
        <span className="font-medium" style={{color:'rgba(255,255,255,0.75)'}}>{run.modelDisplayName || run.modelUsed}</span>
        {run.thinkingLevel && (
          <span className="px-1.5 py-0.5 rounded font-medium text-[10px]" style={{background:'rgba(130,255,207,0.12)',color:'rgba(130,255,207,0.8)'}}>{run.thinkingLevel}</span>
        )}
        {run.judgeModel && <span className="text-purple-500">judge: {run.judgeModel}</span>}
        <span>{(run.durationMs / 1000).toFixed(1)}s</span>
        <span>{run.totalTokens?.toLocaleString()} tokens</span>
        {run.estimatedCost > 0 && <span>${parseFloat(run.estimatedCost).toFixed(4)}</span>}
        <span style={{color:'rgba(255,255,255,0.4)'}}>{new Date(run.createdAt).toLocaleString()}</span>
      </div>
    </div>

    {/* Judge Analysis */}
    {run.judgeAnalysis && (
      <div className="rounded-lg p-3" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)'}}>
        <h4 className="text-xs font-semibold uppercase tracking-wide mb-1.5" style={{color:'rgba(255,255,255,0.45)'}}>Judge Analysis</h4>
        <p className="text-sm leading-relaxed" style={{color:'rgba(255,255,255,0.75)'}}>{run.judgeAnalysis}</p>
      </div>
    )}

    {/* Criteria Results */}
    {run.criteriaResults?.length > 0 && (
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wide mb-2" style={{color:'rgba(255,255,255,0.45)'}}>
          Criteria ({run.criteriaResults.filter(c => c.met).length}/{run.criteriaResults.length} passed)
        </h4>
        <table className="w-full text-sm">
          <thead>
            <tr style={{borderBottom:'1px solid var(--tqa-border-dark)'}}>
              <th className="text-left py-2 pr-3 text-xs font-medium w-8" style={{color:'rgba(255,255,255,0.45)'}}></th>
              <th className="text-left py-2 pr-3 text-xs font-medium" style={{color:'rgba(255,255,255,0.45)'}}>Criterion</th>
              <th className="text-left py-2 text-xs font-medium" style={{color:'rgba(255,255,255,0.45)'}}>Explanation</th>
            </tr>
          </thead>
          <tbody>
            {run.criteriaResults.map((cr, idx) => (
              <tr key={idx} style={{borderBottom:'1px solid rgba(255,255,255,0.06)'}}>
                <td className="py-2 pr-3">
                  <span className={`font-bold text-lg ${cr.met ? 'text-green-500' : 'text-red-500'}`}>
                    {cr.met ? '✓' : '✗'}
                  </span>
                </td>
                <td className="py-2 pr-3 font-medium" style={{color:'rgba(255,255,255,0.8)'}}>{cr.criterion}</td>
                <td className="py-2 text-xs" style={{color:'rgba(255,255,255,0.55)'}}>{cr.explanation}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {/* Quality Dimensions */}
    {run.qualityDimensions?.length > 0 && (
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wide mb-2" style={{color:'rgba(255,255,255,0.45)'}}>Quality Dimensions</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {run.qualityDimensions.map((qd, idx) => (
            <div key={idx} className={`p-3 rounded-lg text-sm border`} style={scoreBg(qd.score)}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold" style={{color:'rgba(255,255,255,0.85)'}}>{qd.dimension}</span>
                <span className={`font-bold text-lg`} style={scoreColor(qd.score)}>{qd.score}</span>
              </div>
              {qd.explanation && <p className="text-xs leading-relaxed" style={{color:'rgba(255,255,255,0.55)'}}>{qd.explanation}</p>}
            </div>
          ))}
        </div>
      </div>
    )}

    {/* Actual Output */}
    {run.actualOutput && (
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wide mb-2" style={{color:'rgba(255,255,255,0.45)'}}>Output</h4>
        <div className="rounded-lg p-3 max-h-72 overflow-y-auto" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)'}}>
          <pre className="text-xs whitespace-pre-wrap font-mono leading-relaxed" style={{color:'rgba(255,255,255,0.75)'}}>  
            {run.actualOutput.length > 3000
              ? run.actualOutput.substring(0, 3000) + `\n\n[Truncated – ${run.actualOutput.length} chars total]`
              : run.actualOutput}
          </pre>
        </div>
      </div>
    )}
  </div>
);

/* ── Comparison View ─────────────────────────────────────── */
const ComparisonView = ({ runs, selectedRunIds }) => {
  const selectedRuns = selectedRunIds.map(id => runs.find(r => r.id === id)).filter(Boolean);

  const allCriteria = [];
  const seenCriteria = new Set();
  selectedRuns.forEach(run => {
    run?.criteriaResults?.forEach(cr => {
      if (!seenCriteria.has(cr.criterion)) {
        seenCriteria.add(cr.criterion);
        allCriteria.push(cr.criterion);
      }
    });
  });

  const allDims = [];
  const seenDims = new Set();
  selectedRuns.forEach(run => {
    run?.qualityDimensions?.forEach(qd => {
      if (!seenDims.has(qd.dimension)) {
        seenDims.add(qd.dimension);
        allDims.push(qd.dimension);
      }
    });
  });

  return (
    <div className="space-y-4">
      <h4 className="text-xs font-semibold uppercase tracking-wide" style={{color:'rgba(255,255,255,0.45)'}}>
        Comparing {selectedRuns.length} runs
      </h4>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr style={{borderBottom: '2px solid rgba(130,255,207,0.3)'}}>
              <th className="text-left py-2.5 pr-4 font-semibold w-40" style={{color:'var(--tqa-green)'}}>Metric</th>
              {selectedRuns.map(run => (
                <th key={run.id} className="text-center py-2.5 px-3 font-semibold min-w-[150px]" style={{color:'var(--tqa-green)'}}>
                  <div className="text-xs text-white">{run.modelDisplayName || run.modelUsed}</div>
                  <div className="text-[10px] font-normal mt-0.5" style={{color:'rgba(130,255,207,0.7)'}}>
                    {run.thinkingLevel && <span className="mr-1">{run.thinkingLevel}</span>}
                    {new Date(run.createdAt).toLocaleDateString()}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Metrics */}
            {[
              { label: 'Score', render: (r) => getScoreBadge(r.score, r.passed) },
              { label: 'Duration', render: (r) => `${(r.durationMs / 1000).toFixed(1)}s` },
              { label: 'Tokens', render: (r) => r.totalTokens?.toLocaleString() || '-' },
              { label: 'Cost', render: (r) => r.estimatedCost > 0 ? `$${parseFloat(r.estimatedCost).toFixed(4)}` : '-' },
              { label: 'Judge Model', render: (r) => r.judgeModel || '-' },
            ].map(({ label, render }) => (
              <tr key={label} style={{borderBottom:'1px solid rgba(255,255,255,0.06)'}}>
                <td className="py-2 pr-4 font-medium" style={{color:'rgba(255,255,255,0.7)'}}>{label}</td>
                {selectedRuns.map(run => (
                  <td key={run.id} className="py-2 px-3 text-center" style={{color:'rgba(255,255,255,0.6)'}}>  
                    {render(run)}
                  </td>
                ))}
              </tr>
            ))}

            {/* Criteria Results */}
            {allCriteria.length > 0 && (
              <>
                <tr>
                  <td colSpan={selectedRuns.length + 1} className="pt-4 pb-1.5 font-semibold text-xs uppercase tracking-wide" style={{color:'var(--tqa-green)'}}>
                    Criteria Results
                  </td>
                </tr>
                {allCriteria.map(criterion => (
                  <tr key={criterion} style={{borderBottom:'1px solid rgba(255,255,255,0.06)'}}>
                    <td className="py-2 pr-4 truncate max-w-[200px]" style={{color:'rgba(255,255,255,0.65)'}} title={criterion}>{criterion}</td>
                    {selectedRuns.map(run => {
                      const cr = run?.criteriaResults?.find(c => c.criterion === criterion);
                      return (
                        <td key={run.id} className="py-2 px-3 text-center">
                          {cr ? (
                            <span className={`font-bold text-base ${cr.met ? 'text-green-500' : 'text-red-500'}`}>
                              {cr.met ? '✓' : '✗'}
                            </span>
                          ) : <span style={{color:'rgba(255,255,255,0.3)'}}>-</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </>
            )}

            {/* Quality Dimensions */}
            {allDims.length > 0 && (
              <>
                <tr>
                  <td colSpan={selectedRuns.length + 1} className="pt-4 pb-1.5 font-semibold text-xs uppercase tracking-wide" style={{color:'var(--tqa-green)'}}>
                    Quality Dimensions
                  </td>
                </tr>
                {allDims.map(dim => (
                  <tr key={dim} style={{borderBottom:'1px solid rgba(255,255,255,0.06)'}}>
                    <td className="py-2 pr-4" style={{color:'rgba(255,255,255,0.65)'}}>{dim}</td>
                    {selectedRuns.map(run => {
                      const qd = run?.qualityDimensions?.find(d => d.dimension === dim);
                      return (
                        <td key={run.id} className="py-2 px-3 text-center">
                          {qd ? (
                            <span className="font-bold" style={scoreColor(qd.score).color ? scoreColor(qd.score) : {color: scoreColor(qd.score)}}>{qd.score}</span>
                          ) : <span style={{color:'rgba(255,255,255,0.3)'}}>-</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ── Main Component ──────────────────────────────────────── */
const EvalRunDetail = ({ runs, activeRunId, selectedRunIds, showComparison }) => {
  const activeRun = runs.find(r => r.id === activeRunId);

  if (showComparison && selectedRunIds.length >= 2) {
    return (
      <div className="flex-1 overflow-y-auto p-5" style={{background:'var(--tqa-surface)'}}>
        <ComparisonView runs={runs} selectedRunIds={selectedRunIds} />
      </div>
    );
  }

  if (!activeRun) {
    return (
      <div className="flex-1 flex items-center justify-center" style={{background:'var(--tqa-surface)',color:'rgba(255,255,255,0.35)'}}>  
        <div className="text-center">
          <svg className="w-12 h-12 mx-auto mb-3" style={{color:'rgba(255,255,255,0.2)'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p className="text-sm">Select a run to view details</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-5" style={{background:'var(--tqa-surface)'}}>
      <SingleRunView run={activeRun} />
    </div>
  );
};

export default EvalRunDetail;
